/* TASK BOARD — a Kanban board per person (Max, 2026-10-30: "làm giúp tôi module này để tôi và những người khác có thể quản lý
   công việc bằng cách tự tạo task, hoặc được assign task từ người khác. Task thì có thể share để ai có thể nhìn và chỉnh
   sửa"; the board he showed: coloured columns, white cards with the person's face, dates and labels).

   Five columns — Backlog · To do · In progress · Review · Done. A card is dragged between and within them (only where the
   reader may edit). The board picker: my tasks (made by me or given to me), each board somebody shared with me, and the
   single tasks shared with me. Who sees what is decided by the server (taskBoard.js), never here.

   Everything that draws a card or a column is a RENDER FUNCTION, not a component declared inside this one — a component
   made inside a component is a new type on every render and re-mounts on each refresh (GCC's reaction tray, 29/10). */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Share2, Search, Calendar, MessageSquare, CheckSquare, X, Trash2, Lock, Inbox, ListTodo, Loader, Eye, CircleCheck, Users, Timer, SquareKanban, ChartGantt, AlignLeft, ListChecks, Flag, UserRound, Tag, Columns3, Repeat, Paperclip, Upload, FileText, CalendarDays, CalendarClock, Coffee, Pencil, ChevronLeft, ChevronRight, Mail, Sparkles, RefreshCw, ExternalLink } from 'lucide-react';
import Header from './Header';
import ModuleHelp from './ModuleHelp';
import UserAvatar from './UserAvatar';
import TaskTimeline, { addPeriod } from './TaskTimeline';
import RichText from './RichText';
import { BACKEND_URL } from './backendConfig';
import './ProjectApproval.css';
import './TaskBoard.css';

const API = `${BACKEND_URL}/api/tasks`;
const COLS = [
  { key: 'backlog', icon: Inbox }, { key: 'todo', icon: ListTodo }, { key: 'doing', icon: Loader },
  { key: 'review', icon: Eye }, { key: 'done', icon: CircleCheck },
];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDay = (d) => { if (!d) return ''; const [y, m, dd] = String(d).slice(0, 10).split('-'); return `${dd}/${MONTHS[+m - 1]}/${y}`; };
const fmtAt = (v) => { if (!v) return ''; const d = new Date(new Date(v).getTime() + 7 * 3600000).toISOString(); return `${d.slice(8, 10)}/${MONTHS[+d.slice(5, 7) - 1]} ${d.slice(11, 16)}`; };
const todayVn = () => new Date(Date.now() + 7 * 3600000).toISOString().slice(0, 10);
const call = async (method, path, body) => {
  const r = await fetch(`${API}${path}`, { method, credentials: 'include', headers: body ? { 'Content-Type': 'application/json' } : undefined, body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) { const e = new Error(j.error || `HTTP ${r.status}`); e.status = r.status; e.body = j; throw e; }
  return j;
};
/* a popup label with its icon — the icon wears the task's stage colour (Max, 30/10: "thêm 1 xíu màu sắc") */
const lab = (Icon, text) => <label className="tb-lab"><Icon size={13} /> {text}</label>;
const repeatLabel = (t, freq, every) => (every > 1 ? t('xTask.r_everyN', { n: every, unit: t(`xTask.u_${freq}`) }) : t(`xTask.r_${freq}`));
const PRIO_RANK = { urgent: 0, high: 1, normal: 2, low: 3 };   // Low since 03/11 (Jay via #1176)
const BLANK = { title: '', body: '', status: 'todo', priority: 'normal', due: '', noRush: false, planned: '', requested: '', estDays: '', repeat: '', repeatEvery: 1, repeatUntil: '', assigneeId: '', tags: '', checklist: [], shares: [] };

/* A person box you type into (Max, 30/10: "tôi muốn đánh thì hiện gợi ý ra để chọn" — a select of ~150 names meant
   scrolling). Letters match anywhere in the name, accents ignored ("nguyen" finds "Nguyễn"); a match at the start of a word
   comes first. ↑ ↓ Enter pick, Esc closes the list (the popup's own Esc waits while a list is open). With `value` it is a
   choice (Given to) showing the chosen name; without, it adds and empties itself (Shared with).
   The list is position: fixed under — or above, near the bottom — the box, so the popup's scroll cannot clip it. */
const fold = (v) => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[đĐ]/g, 'd').toLowerCase();
function PersonPicker({ people, known = [], value, onPick, placeholder, noneLabel, noAccessLabel, disabled, testId, top = [], topLabel }) {
  const [text, setText] = useState(null);         // null = not typing: the box shows the chosen name
  const [hi, setHi] = useState(0);
  const [box, setBox] = useState(null);
  const inRef = useRef(null);
  // the chosen one may be outside the list on offer (no longer holding the grant) — `known` still names them
  const chosen = value ? (people.find((p) => p.id === Number(value)) || known.find((p) => p.id === Number(value))) : null;
  const open = text !== null;
  const q = fold(text);
  const rows = useMemo(() => {
    if (!open) return [];
    /* the person's most picked first (Jay 30/10: "top 3, then everyone else A–Z" — `top` from /people frequent), also among
       the matches while typing */
    const rank = new Map(top.map((id, i) => [id, i]));
    const hits = people.map((p) => { const n = fold(p.name); const i = n.indexOf(q); return { p, i, word: i === 0 || (i > 0 && n[i - 1] === ' '), f: rank.has(p.id) ? rank.get(p.id) : 99 }; })
      .filter((x) => !q || x.i >= 0)
      .sort((a, b) => (q ? (b.word - a.word) : 0) || (a.f - b.f) || a.p.name.localeCompare(b.p.name))
      .slice(0, q ? 8 : 40).map((x) => ({ ...x.p, top: x.f < 99 }));
    const lastTop = hits.reduce((n, x, i) => (x.top ? i : n), -1);
    const marked = hits.map((x, i) => ({ ...x, topEnd: i === lastTop && i < hits.length - 1 }));
    return noneLabel && !q ? [{ id: '', name: noneLabel, none: true }, ...marked] : marked;
  }, [people, q, open, noneLabel, top]);
  const place = () => {
    const r = inRef.current?.getBoundingClientRect(); if (!r) return;
    const up = window.innerHeight - r.bottom < 300 && r.top > 300;
    setBox({ left: r.left, width: r.width, ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
  };
  const start = (v) => { setText(v); setHi(0); place(); };
  const pick = (p) => { onPick(p.none ? '' : p.id); setText(null); if (!value) inRef.current?.blur(); };
  const onKey = (e) => {
    if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter') { e.preventDefault(); start(''); } return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (rows[hi]) pick(rows[hi]); }
    else if (e.key === 'Escape') { e.preventDefault(); setText(null); }
  };
  return (
    <div className="tb-pick" data-tb-pick-open={open ? '1' : undefined}>
      <Search size={13} className="tb-pick-ico" />
      <input ref={inRef} className="tb-in" value={open ? text : (chosen ? chosen.name : '')} disabled={disabled}
        placeholder={chosen ? chosen.name : (value === '' && noneLabel ? noneLabel : placeholder)}
        onFocus={() => start('')} onChange={(e) => start(e.target.value)} onBlur={() => setText(null)} onKeyDown={onKey} data-tb-f={testId} />
      {open && box && ReactDOM.createPortal(
        <div className="tb-pick-list" style={{ position: 'fixed', ...box }} role="listbox" data-tb-picklist={testId}>
          {rows.length ? rows.map((p, i) => (
            <div key={p.id || 'none'} role="option" aria-selected={i === hi} className={`tb-pick-row${i === hi ? ' hi' : ''}${p.none ? ' none' : ''}${p.topEnd ? ' top-end' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(p); }} onMouseEnter={() => setHi(i)} data-tb-pickrow={p.id}>
              {!p.none && <UserAvatar url={p.avatar} name={p.name} size={20} />}<span>{p.name}</span>
              {p.top && topLabel && <em className="tb-pick-top">{topLabel}</em>}
              {noAccessLabel && p.access === false && <em className="tb-pick-noacc">{noAccessLabel}</em>}
            </div>
          )) : <div className="tb-pick-row none">—</div>}
        </div>, document.body)}
    </div>
  );
}

/* the editor: a new task, or one opened from the board (read-only when shared to read) */
function TaskModal({ t, id, people, meId, initialStatus, onClose, onChanged, onNote, frequent = {}, prefill = null, onCreated = null, focusComment = null }) {
  const [task, setTask] = useState(null);
  const [form, setForm] = useState(null);
  const [err, setErr] = useState('');
  /* a link to a task that cannot be opened (task "Add notifications", 02/11: "When a user clicks on a deleted task, the system
     should display a pop-up"): { deleted, title, by, at } from the server's 410, or { missing } — before this, the popup
     simply never appeared and the click looked broken */
  const [gone, setGone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState('');
  const [newCheck, setNewCheck] = useState('');
  const [cEdit, setCEdit] = useState(null);         // { id, text } — my comment being changed (Jay 30/10)
  /* a comment notification opens the card AT the comment (Jay 01/11, task "clicking a notification sometimes does not open
     the item"): scrolled into view and marked for a moment */
  const [flash, setFlash] = useState(null);
  useEffect(() => {
    if (!focusComment || !task || !(task.comments || []).some((c) => c.id === focusComment)) return undefined;
    const el = document.querySelector(`[data-tb-comment="${focusComment}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setFlash(focusComment);
    const h = setTimeout(() => setFlash(null), 2500);
    return () => clearTimeout(h);
  }, [focusComment, task]);
  const load = useCallback(async () => {
    // a suggestion from email or chat (taskInbox) arrives as `prefill`: the ordinary new-task popup, filled in, his Create decides
    if (!id) { setTask({ canEdit: true, canDelete: false, comments: [], log: [] }); setForm({ ...BLANK, requested: todayVn(), status: initialStatus || 'todo', ...(prefill || {}) }); return; }
    try {
      const d = await call('GET', `/${id}`);
      setTask(d);
      setForm({ title: d.title, body: d.body || '', status: d.status, priority: d.priority, due: d.due || '', noRush: !!d.noRush, planned: d.planned || '', requested: d.requested || '', estDays: d.estDays ?? '', repeat: d.repeatFreq || '', repeatEvery: d.repeatEvery || 1, repeatUntil: d.repeatUntil || '', assigneeId: d.assigneeId || '',
        tags: (d.tags || []).join(', '), checklist: d.checklist.map((c) => ({ text: c.text, done: c.done })), shares: d.shares.map((s) => ({ id: s.id, canEdit: s.canEdit })) });
    } catch (e) {
      if (e.status === 410) setGone(e.body || { deleted: true });
      else if (e.status === 404) setGone({ missing: true });
      else setErr(e.message);
    }
  }, [id, initialStatus, prefill]);
  useEffect(() => { load(); }, [load]);
  // the delete question is the portal's own small dialog, not the browser's confirm (Max, 30/10); Esc closes it first
  const [askDel, setAskDel] = useState(false);
  const askDelRef = useRef(false);
  askDelRef.current = askDel;
  useEffect(() => {
    const key = (e) => { if (e.key === 'Escape') { if (e.target?.closest?.('[data-tb-pick-open], [data-tb-cedit]')) return; e.stopImmediatePropagation(); if (askDelRef.current) setAskDel(false); else onClose(); } };
    document.addEventListener('keydown', key, true);
    return () => document.removeEventListener('keydown', key, true);
  }, [onClose]);
  const ro = task && !task.canEdit;
  const setBody = useCallback((v) => setForm((f) => (f ? { ...f, body: v } : f)), []);
  /* FILES AND IMAGES (Max 30/10: "đính kèm ảnh hoặc file") — the button, a drop on the popup, or Ctrl+V of a screenshot.
     On a saved task each goes up at once; on a new one they wait (`pending`) and go up right after it is created. */
  const [pending, setPending] = useState([]);
  const [dropOn, setDropOn] = useState(false);
  const putFile = async (tid, f) => {
    const r = await fetch(`${API}/${tid}/files?name=${encodeURIComponent(f.name || 'image.png')}&type=${encodeURIComponent(f.type || '')}`,
      { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/octet-stream' }, body: f });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || (r.status === 413 ? t('xTask.f_tooBig') : `HTTP ${r.status}`));
  };
  const addFiles = async (list) => {
    const files = [...(list || [])].filter((f) => f && f.size);
    if (!files.length || ro) return;
    const big = files.find((f) => f.size > 20 * 1024 * 1024);
    if (big) { setErr(t('xTask.f_tooBig')); return; }
    if (!id) { setPending((ps) => [...ps, ...files.map((f) => Object.assign(f, { preview: /^image\//.test(f.type) ? URL.createObjectURL(f) : '' }))]); return; }
    setBusy(true); setErr('');
    try { for (const f of files) await putFile(id, f); await load(); onChanged(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const removeFile = async (f) => {
    try { await call('DELETE', `/${id}/files/${f.id}`); await load(); onChanged(); } catch (e) { setErr(e.message); }
  };
  const kb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
  const fileTile = (f, href, onRemove) => (
    <div key={f.id} className={`tb-file${f.image ? ' img' : ''}`} data-tb-file={f.name}>
      {(() => {
        const inner = [f.image && href ? <img key="i" src={href} alt={f.name} /> : <FileText key="i" size={22} />,
          <span key="n" className="tb-file-name">{f.name}</span>, <span key="s" className="tb-file-size">{f.pending ? t('xTask.f_pending') : kb(f.bytes)}</span>];
        // a file still waiting for Create has nowhere to open yet
        return f.pending ? <div className="tb-file-in" title={f.name}>{inner}</div>
          : <a className="tb-file-in" href={href} target="_blank" rel="noopener noreferrer" title={f.name}>{inner}</a>;
      })()}
      {!ro && <button type="button" className="tb-file-x" onClick={onRemove} aria-label={t('xTask.remove')} data-tb-fileremove={f.name}><X size={12} /></button>}
    </div>
  );
  const onPasteFiles = (e) => {
    const files = [...((e.clipboardData && e.clipboardData.files) || [])];
    if (files.length && !ro) { e.preventDefault(); addFiles(files.map((f, i) => (f.name && f.name !== 'image.png' ? f : new File([f], `screenshot-${Date.now()}${i ? `-${i}` : ''}.png`, { type: f.type || 'image/png' })))); }
  };
  // Ctrl/⌘ + Enter saves from anywhere in the popup
  const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !ro) { e.preventDefault(); save(false); } };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  /* everyone this task names: the people on offer, and the task's own share / assignee rows the server named from the
     accounts — Max 30/10, after the grant narrowed to Admin, people shared before showed "(no name)" */
  const known = [...people, ...((task && task.shares) || []).filter((x) => x.name), ...(task && task.assignee ? [task.assignee] : [])];
  const personOf = (pid) => known.find((p) => p.id === Number(pid)) || {};
  const nameOfId = (pid) => personOf(pid).name || t('xTask.nameLess');
  /* "Create & add another" (found filling a board by hand, 30/10): the popup stays, cleared, with the same stage and tags, so ten tasks are ten titles rather than ten trips to the "New task" button */
  const titleRef = useRef(null);
  const [made, setMade] = useState(0);
  const save = async (again = false) => {
    if (!form.title.trim()) { setErr(t('xTask.needTitle')); return; }
    setBusy(true); setErr('');
    try {
      const body = { ...form, assigneeId: form.assigneeId ? Number(form.assigneeId) : null, tags: form.tags, due: form.noRush ? null : (form.due || null),
        noRush: !!form.noRush, planned: form.noRush ? (form.planned || null) : null, requested: form.requested || null, estDays: form.estDays === '' ? null : Number(form.estDays),
        repeat: form.repeat ? { freq: form.repeat, every: Number(form.repeatEvery) || 1, until: form.repeatUntil || null } : null };
      const res = id ? await call('PUT', `/${id}`, body) : await call('POST', '', body);
      if (!id && res.id && pending.length) {
        try { for (const f of pending) await putFile(res.id, f); } catch (e) { setErr(e.message); onChanged(); setBusy(false); return; }
        setPending([]);
      }
      if (!id && res.id && onCreated) onCreated(res.id);
      if (res.next) onNote(res.next);
      onChanged();
      if (again && !id) {
        // priority goes back to Normal: an Urgent carried into the next task went unnoticed (44 tasks typed, 30/10)
        setForm((f) => ({ ...BLANK, requested: f.requested, status: f.status, tags: f.tags, assigneeId: f.assigneeId }));
        setMade((n) => n + 1);
        setTimeout(() => titleRef.current?.focus(), 0);
      } else onClose();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const del = async () => {
    setBusy(true);
    try { await call('DELETE', `/${id}`); onChanged(); onClose(); } catch (e) { setErr(e.message); setBusy(false); }
  };
  const sendComment = async () => {
    if (!comment.trim()) return;
    try { await call('POST', `/${id}/comments`, { body: comment }); setComment(''); await load(); onChanged(); } catch (e) { setErr(e.message); }
  };
  /* SEVERAL LINES PASTED = SEVERAL ITEMS (Jay 30/10: "Could you make the Checklist split pasted text by line? Each line (Enter)
     becomes its own item, and empty lines are skipped"). A list copied from a mail or a doc keeps its bullet or tick box in
     front — "- ", "• ", "☐ ", "[ ] " — and that mark is dropped; a number ("1.") is kept, it may be the item's own order.
     What was already typed in the box joins the paste where the caret is. One line pasted is ordinary typing. */
  const pasteChecks = (e) => {
    const text = (e.clipboardData && e.clipboardData.getData('text/plain')) || '';
    if (!/[\r\n]/.test(text.trim())) return;
    e.preventDefault();
    const el = e.target;
    const whole = `${newCheck.slice(0, el.selectionStart ?? newCheck.length)}${text}${newCheck.slice(el.selectionEnd ?? newCheck.length)}`;
    const items = whole.split(/\r?\n|\r/).map((l) => l.replace(/^\s*(?:[-*•◦▪‣·]|\[[ xX]?\]|[☐☑✓✔])\s+/, '').trim()).filter(Boolean)
      .map((l) => ({ text: l.slice(0, 300), done: false }));
    if (items.length) set('checklist', [...form.checklist, ...items]);
    setNewCheck('');
  };
  /* my own comment, changed in place (Jay 30/10: "Please put comment edit button.") — Enter saves, Esc puts it back */
  const saveCEdit = async () => {
    const text = (cEdit?.text || '').trim();
    if (!text) return;
    try { await call('PUT', `/${id}/comments/${cEdit.id}`, { body: text }); setCEdit(null); await load(); onChanged(); } catch (e) { setErr(e.message); }
  };
  if (gone) {
    const when = gone.at ? fmtAt(gone.at) : '';
    return ReactDOM.createPortal(
      <div className="tb-overlay tb-confirm-back" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="tb-confirm" role="alertdialog" aria-modal="true" aria-labelledby="tb-gone-h" aria-describedby="tb-gone-p" data-tb-gone={gone.deleted ? 'deleted' : 'missing'}
          onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}>
          <div className="tb-confirm-icon">{gone.deleted ? <Trash2 size={20} /> : <X size={20} />}</div>
          <h4 id="tb-gone-h">{gone.deleted ? t('xTask.goneTitle') : t('xTask.missingTitle')}</h4>
          <p id="tb-gone-p">{gone.deleted
            ? (gone.by ? t('xTask.goneBody', { title: gone.title, by: gone.by, at: when }) : t('xTask.goneBodyNoBy', { title: gone.title }))
            : t('xTask.missingBody')}</p>
          <div className="tb-confirm-foot">
            <button type="button" className="pa-btn primary" autoFocus onClick={onClose} data-tb-gone-ok="1">{t('xTask.gotIt')}</button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }
  if (!form) return null;
  const shareIds = new Set(form.shares.map((s) => s.id));
  return ReactDOM.createPortal(
    <div className="tb-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`tb-modal s-${form.status}`} role="dialog" aria-modal="true" data-tb-modal={id || 'new'} onKeyDown={onKey} onPaste={onPasteFiles}
        onDragOver={(e) => { if (ro || ![...(e.dataTransfer?.types || [])].includes('Files')) return; e.preventDefault(); setDropOn(true); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropOn(false); }}
        onDrop={(e) => { if (ro || !e.dataTransfer?.files?.length) return; e.preventDefault(); setDropOn(false); addFiles(e.dataTransfer.files); }}>
        <div className="tb-modal-head">
          <input ref={titleRef} className="tb-title-in" value={form.title} placeholder={t('xTask.f_title')} readOnly={ro} autoFocus={!id}
            onChange={(e) => set('title', e.target.value)} data-tb-f="title" />
          <button type="button" className="tb-x" onClick={onClose} aria-label={t('xTask.close')}><X size={18} /></button>
        </div>
        {ro && <div className="tb-ro"><Lock size={13} /> {t('xTask.readOnly')}</div>}
        {task.owner && <div className="tb-owner">{t('xTask.f_owner', { name: task.owner.name })} · {fmtAt(task.createdAt)}</div>}
        <div className="tb-modal-body">
          <div className="tb-main">
            {lab(AlignLeft, t('xTask.f_body'))}
            {/* formatting + links (Max / Jay 30/10) — the ticket note's editor; links open in a new tab */}
            <div className="tb-rich" data-tb-f="body"><RichText value={form.body} onChange={setBody} disabled={ro} links minHeight={110} /></div>
            {lab(Paperclip, t('xTask.f_files'))}
            <div className={`tb-files${dropOn ? ' drop' : ''}`} data-tb-files="1">
              {(task.files || []).map((f) => fileTile(f, `${API}/files/${f.id}`, () => removeFile(f)))}
              {pending.map((f, i) => fileTile({ id: `p${i}`, name: f.name, image: /^image\/(png|jpeg|gif|webp)$/.test(f.type), bytes: f.size, pending: true },
                f.preview, () => setPending((ps) => ps.filter((_, k) => k !== i))))}
              {!ro && (
                <label className="tb-file-add" title={t('xTask.f_filesHint')}>
                  <Upload size={14} /> {t('xTask.f_attach')}
                  <input type="file" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-tb-fileinput="1" />
                </label>
              )}
              {!ro && <div className="tb-hint tb-files-hint">{t('xTask.f_filesHint')}</div>}
            </div>
            {lab(ListChecks, t('xTask.f_checklist'))}
            <div className="tb-checks">
              {form.checklist.map((c, i) => (
                <div key={i} className={`tb-check${c.done ? ' done' : ''}`}>
                  <input type="checkbox" checked={c.done} disabled={ro} onChange={(e) => set('checklist', form.checklist.map((x, k) => (k === i ? { ...x, done: e.target.checked } : x)))} />
                  <span>{c.text}</span>
                  {!ro && <button type="button" className="tb-mini" onClick={() => set('checklist', form.checklist.filter((_, k) => k !== i))} aria-label={t('xTask.remove')}><X size={12} /></button>}
                </div>
              ))}
              {!ro && (
                <input className="tb-in" value={newCheck} placeholder={`+ ${t('xTask.f_addCheck')}`} data-tb-f="check"
                  onChange={(e) => setNewCheck(e.target.value)} onPaste={pasteChecks}
                  onKeyDown={(e) => { if (e.key === 'Enter' && newCheck.trim()) { e.preventDefault(); set('checklist', [...form.checklist, { text: newCheck.trim(), done: false }]); setNewCheck(''); } }} />
              )}
            </div>
            {id && (
              <>
                {lab(MessageSquare, t('xTask.f_comments'))}
                <div className="tb-comments">
                  {task.comments.map((c) => (
                    <div key={c.id} className={`tb-comment${flash === c.id ? ' tb-comment-flash' : ''}`} data-tb-comment={c.id}>
                      <UserAvatar url={c.avatar} name={c.userName} size={24} />
                      {cEdit && cEdit.id === c.id ? (
                        <div className="tb-comment-edit" data-tb-cedit={c.id}>
                          <b>{c.userName}</b>
                          <textarea className="tb-in" rows={2} autoFocus value={cEdit.text} onChange={(e) => setCEdit({ ...cEdit, text: e.target.value })} data-tb-f="cedit"
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveCEdit(); } if (e.key === 'Escape') { e.preventDefault(); setCEdit(null); } }} />
                          <div className="tb-comment-edit-foot">
                            <span className="tb-hint">{t('xTask.c_editHint')}</span>
                            <button type="button" className="pa-btn small" onClick={() => setCEdit(null)}>{t('xTask.cancel')}</button>
                            <button type="button" className="pa-btn small primary" onClick={saveCEdit} disabled={!cEdit.text.trim()} data-tb-csave={c.id}>{t('xTask.save')}</button>
                          </div>
                        </div>
                      ) : (
                        <div className="tb-comment-body">
                          <b>{c.userName}</b> <em>{fmtAt(c.at)}{c.editedAt && <span className="tb-comment-edited" title={fmtAt(c.editedAt)}> · {t('xTask.c_edited')}</span>}</em>
                          {c.mine && !cEdit && (
                            <button type="button" className="tb-comment-editbtn" onClick={() => setCEdit({ id: c.id, text: c.body })} title={t('xTask.c_edit')} data-tb-cedit-open={c.id}>
                              <Pencil size={12} /> {t('xTask.c_edit')}
                            </button>
                          )}
                          <p>{c.body}</p>
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="tb-comment-new">
                    <input className="tb-in" value={comment} placeholder={t('xTask.f_commentPh')} onChange={(e) => setComment(e.target.value)} data-tb-f="comment"
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendComment(); } }} />
                    <button type="button" className="pa-btn small" onClick={sendComment} disabled={!comment.trim()}>{t('xTask.f_send')}</button>
                  </div>
                </div>
                <details className="tb-history"><summary>{t('xTask.f_history')} ({task.log.length})</summary>
                  {task.log.map((l, i) => <div key={i}>{fmtAt(l.at)} · <b>{l.userName}</b> {t(`xTask.log_${l.action}`, l.action)}{l.detail ? ` — ${l.action === 'moved' ? l.detail.replace(/\b(backlog|todo|doing|review|done)\b/g, (k) => t(`xTask.col_${k}`)) : l.detail}` : ''}</div>)}
                </details>
              </>
            )}
          </div>
          <div className="tb-side">
            {lab(Columns3, t('xTask.f_status'))}
            <select className="tb-in tb-stage-in" value={form.status} disabled={ro} onChange={(e) => set('status', e.target.value)} data-tb-f="status">
              {COLS.map((c) => <option key={c.key} value={c.key}>{t(`xTask.col_${c.key}`)}</option>)}
            </select>
            {lab(Flag, t('xTask.f_priority'))}
            <select className={`tb-in tb-prio-in pr-${form.priority}`} value={form.priority} disabled={ro} onChange={(e) => set('priority', e.target.value)} data-tb-f="priority">
              {['low', 'normal', 'high', 'urgent'].map((p) => <option key={p} value={p}>{t(`xTask.p_${p}`)}</option>)}
            </select>
            {lab(Inbox, t('xTask.f_requested'))}
            <input className="tb-in" type="date" value={form.requested} readOnly={ro} onChange={(e) => set('requested', e.target.value)} data-tb-f="requested" />
            {/* NO RUSH (Jay 30/10): "not urgent, schedule it yourself" — no due date; the person doing it sets a planned date */}
            <div className="tb-lab-row">
              {lab(Calendar, t('xTask.f_due'))}
              <label className={`tb-norush-box${form.noRush ? ' on' : ''}`} title={t('xTask.f_noRushHint')}>
                <input type="checkbox" checked={!!form.noRush} disabled={ro} onChange={(e) => setForm((f) => ({ ...f, noRush: e.target.checked, due: e.target.checked ? '' : f.due }))} data-tb-f="noRush" />
                <Coffee size={12} /> {t('xTask.f_noRush')}
              </label>
            </div>
            {form.noRush ? (
              <>
                <input className="tb-in" type="date" value="" disabled aria-label={t('xTask.f_due')} />
                <div className="tb-hint tb-norush-hint">{t('xTask.f_noRushHint')}</div>
                {lab(CalendarClock, t('xTask.f_planned'))}
                <input className="tb-in tb-planned-in" type="date" value={form.planned} readOnly={ro} onChange={(e) => set('planned', e.target.value)} data-tb-f="planned" />
              </>
            ) : (
              <>
                <input className={`tb-in${form.due && form.status !== 'done' ? (form.due < todayVn() ? ' due-late' : form.due === todayVn() ? ' due-today' : '') : ''}`} type="date" value={form.due} readOnly={ro} onChange={(e) => set('due', e.target.value)} data-tb-f="due" />
                {!form.due && <div className="tb-hint">{t('xTask.f_dueNone')}</div>}
              </>
            )}
            {lab(Timer, t('xTask.f_est'))}
            <input className="tb-in" type="number" min="0.5" step="0.5" inputMode="decimal" value={form.estDays} readOnly={ro} placeholder={t('xTask.f_estPh')}
              onChange={(e) => set('estDays', e.target.value)} data-tb-f="est" />
            {lab(Repeat, t('xTask.f_repeat'))}
            <select className={`tb-in${form.repeat ? ' tb-repeat-on' : ''}`} value={form.repeat} disabled={ro} onChange={(e) => set('repeat', e.target.value)} data-tb-f="repeat">
              <option value="">{t('xTask.r_none')}</option>
              {['week', 'month', 'quarter', 'year'].map((f) => <option key={f} value={f}>{t(`xTask.r_${f}`)}</option>)}
            </select>
            {form.repeat && (
              <div className="tb-repeat">
                <label>{t('xTask.r_every')}
                  <input className="tb-in" type="number" min="1" max="12" value={form.repeatEvery} readOnly={ro} onChange={(e) => set('repeatEvery', e.target.value)} data-tb-f="repeatEvery" />
                  {t(`xTask.u_${form.repeat}`)}</label>
                <label>{t('xTask.r_until')}
                  <input className="tb-in" type="date" value={form.repeatUntil} readOnly={ro} onChange={(e) => set('repeatUntil', e.target.value)} data-tb-f="repeatUntil" /></label>
                {/* what happens next, in words — the rule is only trusted when it can be read */}
                <div className={`tb-hint${form.due ? '' : ' warn'}`} data-tb-repeat-next="1">
                  {form.due
                    ? t('xTask.r_next', { date: fmtDay(addPeriod(form.due, form.repeat, Number(form.repeatEvery) || 1)) })
                    : t('xTask.r_needDate')}
                </div>
              </div>
            )}
            {lab(UserRound, t('xTask.f_assignee'))}
            <PersonPicker people={people.filter((p) => p.id !== meId && p.access)} known={known} value={form.assigneeId} noneLabel={t('xTask.f_nobody')} disabled={ro}
              top={frequent.assignee || []} topLabel={t('xTask.frequent')}
              onPick={(v) => set('assigneeId', v)} testId="assignee" />
            {lab(Tag, t('xTask.f_tags'))}
            <input className="tb-in" value={form.tags} readOnly={ro} placeholder={t('xTask.f_tagsPh')} onChange={(e) => set('tags', e.target.value)} data-tb-f="tags" />
            {form.tags.trim() && <div className="tb-tagrow">{[...new Set(form.tags.split(',').map((g) => g.trim()).filter(Boolean))].map((g) => <span key={g} className="tb-tag">{g}</span>)}</div>}
            {lab(Share2, t('xTask.f_share'))}
            <div className="tb-shares">
              {form.shares.map((s) => (
                <div key={s.id} className="tb-share">
                  <UserAvatar url={personOf(s.id).avatar} name={nameOfId(s.id)} size={22} />
                  <span className="tb-share-name">{nameOfId(s.id)}{personOf(s.id).access === false && <em className="tb-share-noacc" title={t('xTask.noAccessHint')}>{t('xTask.noAccess')}</em>}</span>
                  <select value={s.canEdit ? 'edit' : 'view'} disabled={ro} onChange={(e) => set('shares', form.shares.map((x) => (x.id === s.id ? { ...x, canEdit: e.target.value === 'edit' } : x)))}>
                    <option value="view">{t('xTask.f_canView')}</option><option value="edit">{t('xTask.f_canEdit')}</option>
                  </select>
                  {!ro && <button type="button" className="tb-mini" onClick={() => set('shares', form.shares.filter((x) => x.id !== s.id))} aria-label={t('xTask.remove')}><X size={12} /></button>}
                </div>
              ))}
              {!ro && (
                <PersonPicker people={people.filter((p) => p.id !== meId && !shareIds.has(p.id))} placeholder={t('xTask.f_shareAdd')} noAccessLabel={t('xTask.noAccess')}
                  top={frequent.share || []} topLabel={t('xTask.frequent')}
                  onPick={(v) => { if (v) set('shares', [...form.shares, { id: v, canEdit: false }]); }} testId="shareadd" />
              )}
            </div>
          </div>
        </div>
        {err && <div className="tb-err">{err}</div>}
        <div className="tb-modal-foot">
          {id && task.canDelete && <button type="button" className="pa-btn danger small" onClick={() => setAskDel(true)} disabled={busy} data-tb-del="1"><Trash2 size={13} /> {t('xTask.del')}</button>}
          <span className="tb-grow" />
          <button type="button" className="pa-btn" onClick={onClose}>{ro ? t('xTask.close') : t('xTask.cancel')}</button>
          {made > 0 && <span className="tb-made">{t('xTask.madeN', { n: made })}</span>}
          {!ro && !id && <button type="button" className="pa-btn" onClick={() => save(true)} disabled={busy} data-tb-saveagain="1">{t('xTask.createAgain')}</button>}
          {!ro && <button type="button" className="pa-btn primary" onClick={() => save(false)} disabled={busy} data-tb-save="1" title="Ctrl + Enter">{id ? t('xTask.save') : t('xTask.create')}</button>}
        </div>
      </div>
      {askDel && (
        <div className="tb-overlay tb-confirm-back" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) setAskDel(false); }}>
          <div className="tb-confirm" role="alertdialog" aria-modal="true" aria-labelledby="tb-confirm-h" data-tb-confirm="1">
            <div className="tb-confirm-icon"><Trash2 size={20} /></div>
            <h4 id="tb-confirm-h">{t('xTask.delTitle')}</h4>
            <p>{t('xTask.delAsk', { title: task.title })}</p>
            <div className="tb-confirm-foot">
              <button type="button" className="pa-btn" autoFocus onClick={() => setAskDel(false)} data-tb-confirm-no="1">{t('xTask.cancel')}</button>
              <button type="button" className="pa-btn danger" onClick={() => { setAskDel(false); del(); }} disabled={busy} data-tb-confirm-yes="1"><Trash2 size={13} /> {t('xTask.del')}</button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

/* my whole board, shown to the people listed */
function BoardShareModal({ t, people, meId, current, onClose, onSaved }) {
  const [list, setList] = useState(current.map((x) => ({ id: x.id, canEdit: x.canEdit })));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ids = new Set(list.map((x) => x.id));
  const nameOf = (id) => (people.find((p) => p.id === id) || current.find((p) => p.id === id) || {}).name || '';
  const save = async () => {
    setBusy(true); setErr('');
    try { await call('PUT', '/board-share', { people: list }); onSaved(); onClose(); } catch (e) { setErr(e.message); setBusy(false); }
  };
  return ReactDOM.createPortal(
    <div className="tb-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="tb-modal tb-modal-sm" role="dialog" aria-modal="true" data-tb-boardshare="1">
        <div className="tb-modal-head"><h3><Users size={17} /> {t('xTask.boardShareTitle')}</h3>
          <button type="button" className="tb-x" onClick={onClose} aria-label={t('xTask.close')}><X size={18} /></button></div>
        <p className="tb-hint">{t('xTask.boardShareSub')}</p>
        <div className="tb-shares">
          {list.map((s) => (
            <div key={s.id} className="tb-share">
              <span className="tb-share-name">{nameOf(s.id)}</span>
              <select value={s.canEdit ? 'edit' : 'view'} onChange={(e) => setList(list.map((x) => (x.id === s.id ? { ...x, canEdit: e.target.value === 'edit' } : x)))}>
                <option value="view">{t('xTask.f_canView')}</option><option value="edit">{t('xTask.f_canEdit')}</option>
              </select>
              <button type="button" className="tb-mini" onClick={() => setList(list.filter((x) => x.id !== s.id))} aria-label={t('xTask.remove')}><X size={12} /></button>
            </div>
          ))}
          <PersonPicker people={people.filter((p) => p.id !== meId && !ids.has(p.id) && p.access)} placeholder={t('xTask.f_shareAdd')}
            onPick={(v) => { if (v) setList([...list, { id: v, canEdit: false }]); }} testId="boardadd" />
        </div>
        {err && <div className="tb-err">{err}</div>}
        <div className="tb-modal-foot"><span className="tb-grow" />
          <button type="button" className="pa-btn" onClick={onClose}>{t('xTask.cancel')}</button>
          <button type="button" className="pa-btn primary" onClick={save} disabled={busy} data-tb-boardsave="1">{t('xTask.save')}</button></div>
      </div>
    </div>,
    document.body,
  );
}

/* SUGGESTIONS FROM EMAIL, GOOGLE CHAT AND GCC (Max 31/10, taskInbox.js) — Catchy reads his mail and chats and lists the work it
   found; nothing is on the board until he presses Create task, which opens the ordinary new-task popup filled in. Dismiss
   keeps the suggestion from coming back. The Google grant is his own (Connect), checked on the server against his address. */
const SRC_ICON = { gmail: Mail, gchat: MessageSquare, gcc: Users };
function SuggestModal({ t, data, onClose, onReload, onCreate }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [ran, setRan] = useState(null);
  const sug = data?.suggestions || [];
  const connect = async () => {
    setErr('');
    try { const d = await call('GET', `/inbox/connect?back=${encodeURIComponent('/tasks?inbox=1')}`); window.location.href = d.url; } catch (e) { setErr(e.message); }
  };
  const runNow = async () => {
    setBusy('run'); setErr(''); setRan(null);
    try {
      const d = await call('POST', '/inbox/run');
      const r = d.result || {};
      setRan(Object.values(r).reduce((a, x) => ({ read: a.read + (x.read || 0), made: a.made + (x.suggested || 0) }), { read: 0, made: 0 }));
      await onReload();
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };
  const dismiss = async (s) => {
    setBusy(`d${s.id}`); setErr('');
    try { await call('POST', `/inbox/${s.id}/dismiss`); await onReload(); } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };
  const srcLine = (k) => {
    const st = data?.sources?.[k] || {};
    const off = k !== 'gcc' && (!data?.google || (k === 'gchat' && !data.google.chat));
    return (
      <span key={k} className={`tb-sug-src${st.error ? ' bad' : ''}`} data-tb-sugsrc={k}>
        {React.createElement(SRC_ICON[k], { size: 13 })} {t(`xTask.in_src_${k}`)}
        <em>{off ? t('xTask.in_notConnected') : st.error ? t('xTask.in_err', { msg: st.error }) : st.lastRun ? t('xTask.in_lastRun', { when: fmtAt(st.lastRun) }) : '—'}</em>
      </span>
    );
  };
  return ReactDOM.createPortal(
    <div className="tb-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="tb-modal tb-sug-modal" role="dialog" aria-modal="true" data-tb-suggest="1">
        <div className="tb-modal-head"><h3><Sparkles size={17} /> {t('xTask.in_title')}</h3>
          <button type="button" className="tb-x" onClick={onClose} aria-label={t('xTask.close')}><X size={18} /></button></div>
        <p className="tb-hint">{t('xTask.in_sub')}</p>
        <div className="tb-sug-bar">
          <div className="tb-sug-srcs">{['gmail', 'gchat', 'gcc'].map(srcLine)}</div>
          <span className="tb-grow" />
          <button type="button" className="pa-btn" onClick={connect} data-tb-sugconnect="1"><Mail size={14} /> {data?.google ? t('xTask.in_reconnect') : t('xTask.in_connect')}</button>
          <button type="button" className="pa-btn" onClick={runNow} disabled={!!busy} data-tb-sugrun="1"><RefreshCw size={14} className={busy === 'run' ? 'tb-spin' : ''} /> {busy === 'run' ? t('xTask.in_running') : t('xTask.in_runNow')}</button>
        </div>
        {data?.google && <p className="tb-hint">{t('xTask.in_connected', { email: data.google.email })}{!data.google.chat ? ` · ${t('xTask.in_chatOff')}` : ''}</p>}
        {ran && <div className="tb-note" data-tb-sugran="1">{t('xTask.in_ranN', { n: ran.read, s: ran.made })}</div>}
        {err && <div className="tb-err" onClick={() => setErr('')}>{err}</div>}
        <div className="tb-sug-list">
          {!sug.length && <p className="tb-hint tb-sug-none">{t('xTask.in_none')}</p>}
          {sug.map((s) => (
            <div key={s.id} className="tb-sug" data-tb-sug={s.id}>
              <div className="tb-sug-meta">
                <span className={`tb-sug-chip ${s.source}`}>{React.createElement(SRC_ICON[s.source] || Mail, { size: 12 })} {t(`xTask.in_src_${s.source}`)}</span>
                <span className="tb-sug-where">{s.where}</span>
                <span className="tb-sug-from">{t('xTask.in_from', { who: s.from === 'me' ? t('xTask.in_me') : s.from })}</span>
                {s.at && <span className="tb-sug-at">{fmtAt(s.at)}</span>}
              </div>
              <div className="tb-sug-title">{s.title}
                {s.priority !== 'normal' && <span className={`tb-prio ${s.priority}`}>{t(`xTask.p_${s.priority}`)}</span>}
                {s.due && <span className="tb-sug-due"><Calendar size={12} /> {fmtDay(s.due)}</span>}</div>
              {s.body && <p className="tb-sug-body">{s.body}</p>}
              {s.why && <p className="tb-sug-why">{t('xTask.in_why')}: {s.why}</p>}
              {s.excerpt && <details className="tb-sug-ex"><summary>{t('xTask.in_excerpt')}</summary><pre>{s.excerpt}</pre></details>}
              <div className="tb-sug-acts">
                {s.link && <a className="pa-btn" href={s.link} target="_blank" rel="noopener noreferrer" data-tb-sugopen={s.id}><ExternalLink size={14} /> {t('xTask.in_open')}</a>}
                <span className="tb-grow" />
                <button type="button" className="pa-btn" onClick={() => dismiss(s)} disabled={!!busy} data-tb-sugdismiss={s.id}><X size={14} /> {t('xTask.in_dismiss')}</button>
                <button type="button" className="pa-btn primary" onClick={() => onCreate(s)} disabled={!!busy} data-tb-sugcreate={s.id}><Plus size={14} /> {t('xTask.in_create')}</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* MY DAY (Max 30/10: "có 1 cái summary để biết trong ngày tôi đã làm những gì trên module System Project") — one Vietnam
   day of my own work on the board, task by task, from the task log and my comments (/api/tasks/day). Finished tasks come
   first. The number cards are the filter (Max, same day: "mấy cái card có thể bấm và nội dung phía dưới sẽ chạy theo"):
   a card shows the tasks with that kind of work and, under each, only those lines; the same card again shows all. */
const dayShift = (d, n) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10);
const colName = (t, k) => (COLS.some((c) => c.key === k) ? t(`xTask.col_${k}`) : k);
/* a Save before 30/10 logged every field it sent ("title, description, priority normal, due …") whether it changed or not —
   such a line says only that the task was saved */
const wholeSave = (detail) => /^title, description, /.test(detail || '');
const eventText = (t, e) => {
  if (e.action === 'moved') { const [a, b] = String(e.detail || '').split(' → '); return t('xTask.d_moved', { from: colName(t, a), to: colName(t, b) }); }
  if (e.action === 'created') return t('xTask.d_created');
  if (e.action === 'comment') return t('xTask.d_comment', { text: e.detail || '' });
  if (e.action === 'file') return t('xTask.d_file', { name: e.detail || '' });
  if (e.action === 'unfile') return t('xTask.d_unfile', { name: e.detail || '' });
  if (e.action === 'comment_edit') return t('xTask.d_commentEdit');
  if (e.action === 'edited') return wholeSave(e.detail) ? t('xTask.d_saved') : t('xTask.d_edited', { what: e.detail || '' });
  return `${t(`xTask.log_${e.action}`, e.action)}${e.detail ? ` — ${e.detail}` : ''}`;
};
const isFinish = (e) => (e.action === 'moved' && /→ done$/.test(e.detail || '')) || (e.action === 'edited' && !wholeSave(e.detail) && /\bstatus done\b/.test(e.detail || ''));
// a whole-save line that carried "status done" may be the finish too — counted only when the task stands in Done now
const finishedOn = (x) => x.events.some(isFinish) || (x.status === 'done' && x.events.some((e) => e.action === 'edited' && wholeSave(e.detail) && /\bstatus done\b/.test(e.detail || '')));
const DAY_KINDS = [
  { key: 'all', icon: ListTodo, label: 'd_sTasks' },
  { key: 'done', icon: CircleCheck, label: 'd_sDone' },
  { key: 'created', icon: Plus, label: 'd_sNew' },
  { key: 'moved', icon: Columns3, label: 'd_sMoved' },
  { key: 'comment', icon: MessageSquare, label: 'd_sComments' },
  { key: 'file', icon: Paperclip, label: 'd_sFiles' },
];
function DayView({ t, onOpen }) {
  const [d, setD] = useState(todayVn());
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [kind, setKind] = useState('all');
  useEffect(() => { setData(null); setErr(''); call('GET', `/day?date=${d}`).then(setData).catch((e) => setErr(e.message)); }, [d]);
  const list = useMemo(() => {
    const xs = (data?.tasks || []).map((x) => ({ ...x, finished: finishedOn(x) }));
    return [...xs.filter((x) => x.finished), ...xs.filter((x) => !x.finished)];
  }, [data]);
  const counts = useMemo(() => {
    const ev = (a) => list.reduce((n, x) => n + x.events.filter((e) => e.action === a).length, 0);
    return { all: list.length, done: list.filter((x) => x.finished).length, created: ev('created'), moved: ev('moved'), comment: ev('comment'), file: ev('file') };
  }, [list]);
  // the tasks the chosen card speaks of, each with its own lines of that kind
  const shown = useMemo(() => {
    if (kind === 'all') return list;
    if (kind === 'done') return list.filter((x) => x.finished);
    return list.map((x) => ({ ...x, events: x.events.filter((e) => e.action === kind) })).filter((x) => x.events.length);
  }, [list, kind]);
  return (
    <div className="tb-day" data-tb-day={d}>
      <div className="tb-day-bar">
        <button type="button" className="pa-btn small" onClick={() => setD(dayShift(d, -1))} aria-label={t('xTask.d_prev')} title={t('xTask.d_prev')}><ChevronLeft size={14} /></button>
        <input className="tb-in tb-day-date" type="date" value={d} max={todayVn()} onChange={(e) => e.target.value && setD(e.target.value)} data-tb-daydate="1" />
        <button type="button" className="pa-btn small" onClick={() => setD(dayShift(d, 1))} disabled={d >= todayVn()} aria-label={t('xTask.d_next')} title={t('xTask.d_next')}><ChevronRight size={14} /></button>
        {d !== todayVn() && <button type="button" className="pa-btn small" onClick={() => setD(todayVn())}>{t('xTask.d_today')}</button>}
      </div>
      {err && <div className="tb-err">{err}</div>}
      {!data ? <p className="tb-hint">{t('xTask.loading')}</p> : !list.length ? <p className="tb-empty tb-day-empty">{t('xTask.d_none', { date: fmtDay(d) })}</p> : (
        <>
          <div className="tb-day-stats" role="tablist">
            {DAY_KINDS.map(({ key, icon: Icon, label }) => (
              <button key={key} type="button" role="tab" aria-selected={kind === key} disabled={!counts[key] && key !== 'all'}
                className={`tb-day-stat k-${key}${kind === key ? ' on' : ''}`} onClick={() => setKind(kind === key && key !== 'all' ? 'all' : key)} data-tb-daykind={key}>
                <Icon size={16} /><b>{counts[key]}</b><span>{t(`xTask.${label}`)}</span>
              </button>
            ))}
          </div>
          <div className="tb-day-list">
            {shown.map((x) => (
              <div key={x.id} className={`tb-day-task s-${x.status}${x.finished ? ' fin' : ''}`} data-tb-daytask={x.id}>
                <button type="button" className="tb-day-title" onClick={() => onOpen(x.id)}>
                  {x.finished ? <CircleCheck size={15} /> : <CalendarDays size={15} />} <span>{x.title}</span>
                  <em className={`tb-day-stage s-${x.status}`}>{colName(t, x.status)}</em>
                </button>
                <ul>
                  {x.events.map((e, i) => <li key={i} className={`ev-${e.action}${isFinish(e) ? ' ev-fin' : ''}`}><time>{fmtAt(e.at).slice(-5)}</time> {eventText(t, e)}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* THE GUIDE (Max 01/11: every module has one; the Task Board has no manual yet, so the last step points back at the Guide
   button). It walks the Board view. Share my board and New task are drawn only for a person who may create tasks, and
   the Catchy suggestions button only for the pilot account, so those are skipped for everybody else. */
const TOUR = [
  { selector: '.tb-boards', title: 'tourTasks.boardsT', body: 'tourTasks.boardsB' },
  { selector: '.tb-viewseg', title: 'tourTasks.viewsT', body: 'tourTasks.viewsB' },
  { selector: '.tb-search', title: 'tourTasks.searchT', body: 'tourTasks.searchB' },
  { selector: '.tb-board-grid', title: 'tourTasks.columnsT', body: 'tourTasks.columnsB' },
  { selector: '[data-tb-new]', title: 'tourTasks.newT', body: 'tourTasks.newB' },
  { selector: '[data-tb-shareboard]', title: 'tourTasks.shareT', body: 'tourTasks.shareB' },
];

export default function TaskBoard() {
  const { t } = useTranslation();
  const qs0 = new URLSearchParams(window.location.search);
  const [board, setBoard] = useState(qs0.get('board') || 'me');
  const [ppl, setPpl] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [prio, setPrio] = useState('');
  const [whose, setWhose] = useState('');
  /* the order inside a column: the owner's own (drag), or by due date / priority — with 44 tasks the one due tomorrow sat
     tenth in To do (30/10). Remembered per browser. */
  // Board or Timeline (Gantt) — Max 30/10; remembered per browser, and ?view=timeline opens it
  const [view, setViewRaw] = useState(() => { if (['timeline', 'day'].includes(qs0.get('view'))) return qs0.get('view'); try { return localStorage.getItem('tb.view') || 'board'; } catch (e) { return 'board'; } });
  const setView = (v) => { setViewRaw(v); try { localStorage.setItem('tb.view', v); } catch (e) { /* private window */ } };
  const [order, setOrderRaw] = useState(() => { try { return localStorage.getItem('tb.order') || ''; } catch (e) { return ''; } });
  const setOrder = (v) => { setOrderRaw(v); try { localStorage.setItem('tb.order', v); } catch (e) { /* private window */ } };
  const [open, setOpen] = useState(qs0.get('task') ? { id: Number(qs0.get('task')), comment: Number(qs0.get('comment')) || null } : null);
  /* A NOTIFICATION CLICKED WHILE ALREADY HERE (Jay 01/11: "When Jay is on the System Project page and clicks a task
     notification, the card does not open"). The bell navigates within the app, so this page does not mount again and the
     address it read at mount is old news: every navigation that arrives (a new location key, even to the same address) is
     read again here. The page's own address updates use history.replaceState and do not come through this way. */
  const location = useLocation();
  const firstNav = useRef(true);
  useEffect(() => {
    if (firstNav.current) { firstNav.current = false; return; }
    const p = new URLSearchParams(location.search);
    const tid = Number(p.get('task'));
    if (tid) setOpen({ id: tid, comment: Number(p.get('comment')) || null });
    if (p.get('inbox') === '1') setSugOpen(true);
    if (p.get('board')) setBoard(p.get('board'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
  const [shareOpen, setShareOpen] = useState(false);
  // suggestions from email and chat (taskInbox.js) — `inbox.on` only for the pilot account; ?inbox=1 (the bell's link) opens them
  const [inbox, setInbox] = useState(null);
  const [sugOpen, setSugOpen] = useState(qs0.get('inbox') === '1');
  const [fromSug, setFromSug] = useState(null);
  const loadInbox = useCallback(() => call('GET', '/inbox').then(setInbox).catch(() => setInbox(null)), []);
  useEffect(() => { loadInbox(); const h = setInterval(loadInbox, 300000); return () => clearInterval(h); }, [loadInbox]);
  const sugToTask = (sg) => {
    const esc = (v) => String(v || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const href = sg.link ? (/^https?:/.test(sg.link) ? sg.link : `${window.location.origin}${sg.link}`) : '';
    const src = `${t(`xTask.in_src_${sg.source}`)}${sg.where ? ` · ${sg.where}` : ''}${sg.from ? ` · ${sg.from === 'me' ? t('xTask.in_me') : sg.from}` : ''}${sg.at ? ` · ${fmtAt(sg.at)}` : ''}`;
    setSugOpen(false); setFromSug(sg);
    setOpen({ id: null, status: 'todo', prefill: { title: sg.title, priority: sg.priority || 'normal', due: sg.due || '',
      body: `${sg.body ? `<p>${esc(sg.body)}</p>` : ''}<p>${href ? `<a href="${esc(href)}">${esc(src)}</a>` : esc(src)}</p>` } });
  };
  // "the next weekly X is made — due …": a repeating task just went Done (shown 8 s)
  const [note, setNote] = useState(null);
  useEffect(() => { if (!note) return undefined; const h = setTimeout(() => setNote(null), 8000); return () => clearTimeout(h); }, [note]);
  const [quick, setQuick] = useState(null);        // { col, text } — the column's own "+" box
  const [drag, setDrag] = useState(null);          // { id, from }
  const [over, setOver] = useState(null);          // { col, index }
  const colRefs = useRef({});

  const loadPeople = useCallback(() => call('GET', '/people').then(setPpl).catch((e) => setErr(e.message)), []);
  const loadBoard = useCallback(() => call('GET', `?board=${encodeURIComponent(board)}`).then((d) => setTasks(d.tasks)).catch((e) => setErr(e.message)), [board]);
  useEffect(() => { loadPeople(); }, [loadPeople]);
  useEffect(() => { setTasks(null); loadBoard(); }, [loadBoard]);
  useEffect(() => { const h = setInterval(loadBoard, 60000); return () => clearInterval(h); }, [loadBoard]);
  useEffect(() => {
    const u = new URLSearchParams();
    if (board !== 'me') u.set('board', board);
    if (open && open.id) u.set('task', open.id);
    window.history.replaceState({}, '', `/tasks${u.toString() ? `?${u}` : ''}`);
  }, [board, open]);

  const meId = ppl?.me;
  const shown = useMemo(() => (tasks || []).filter((x) => {
    if (q && !`${x.title} ${x.snippet || ''} ${(x.tags || []).join(' ')}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (prio && (x.priority === 'normal' || x.priority === 'low')) return false;
    if (whose === 'given' && x.assigneeId !== meId) return false;
    if (whose === 'made' && x.ownerId !== meId) return false;
    return true;
  }), [tasks, q, prio, whose, meId]);
  const byCol = useMemo(() => {
    const own = (a, b) => a.sort - b.sort || a.id - b.id;
    // a No-rush card sorts by its planned date (Jay 30/10)
    const byDue = (a, b) => (a.due || (a.noRush && a.planned) || '9999').localeCompare(b.due || (b.noRush && b.planned) || '9999');
    const byPrio = (a, b) => PRIO_RANK[a.priority] - PRIO_RANK[b.priority];
    const cmp = order === 'due' ? (a, b) => byDue(a, b) || byPrio(a, b) || own(a, b)
      : order === 'prio' ? (a, b) => byPrio(a, b) || byDue(a, b) || own(a, b) : own;
    return Object.fromEntries(COLS.map((c) => [c.key, shown.filter((x) => x.status === c.key).sort(cmp)]));
  }, [shown, order]);

  const addQuick = async (col) => {
    const title = (quick?.text || '').trim();
    if (!title) { setQuick(null); return; }
    try { await call('POST', '', { title, status: col }); setQuick({ col, text: '' }); loadBoard(); } catch (e) { setErr(e.message); }
  };
  /* where in a column the dragged card would land: the first card whose middle is below the pointer */
  const indexAt = (col, y) => {
    const els = [...(colRefs.current[col]?.querySelectorAll('[data-tb-card]') || [])].filter((el) => Number(el.dataset.tbCard) !== drag?.id);
    const i = els.findIndex((el) => { const r = el.getBoundingClientRect(); return y < r.top + r.height / 2; });
    return i < 0 ? els.length : i;
  };
  const drop = async (col) => {
    if (!drag) return;
    // sorted by date or priority the place in a column is not the owner's: a move between columns goes to the top, and
    // a drop in its own column changes nothing
    if (order) {
      const id = drag.id; setDrag(null); setOver(null);
      if (col === drag.from) return;
      setTasks((ts) => ts.map((x) => (x.id === id ? { ...x, status: col } : x)));
      try { const r = await call('PUT', `/${id}/move`, { status: col, beforeId: null, afterId: null }); if (r.next) setNote(r.next); loadBoard(); } catch (e) { setErr(e.message); loadBoard(); }
      return;
    }
    const list = byCol[col].filter((x) => x.id !== drag.id);
    const index = over && over.col === col ? over.index : list.length;
    const afterId = list[index]?.id || null;              // the card that ends up below
    const beforeId = index > 0 ? list[index - 1].id : null; // the card that ends up above
    setDrag(null); setOver(null);
    // move it on screen at once; the server's order follows
    setTasks((ts) => ts.map((x) => (x.id === drag.id ? { ...x, status: col, sort: beforeId && afterId ? (list[index - 1].sort + list[index].sort) / 2 : beforeId ? list[index - 1].sort + 1 : afterId ? list[index].sort - 1 : 0 } : x)));
    try { const r = await call('PUT', `/${drag.id}/move`, { status: col, beforeId, afterId }); if (r.next) setNote(r.next); loadBoard(); } catch (e) { setErr(e.message); loadBoard(); }
  };

  /* the face on a card is the OTHER person: whom I gave it to, or who gave it to me — on my own board my face on every card
     said nothing (seen filling the board, 30/10). On another person's board it is whoever is doing it. */
  const face = (x) => {
    if (board !== 'me') return x.assignee || x.owner;
    if (x.assignee && x.assigneeId !== meId) return x.assignee;
    if (x.ownerId !== meId) return x.owner;
    return null;
  };
  const card = (x) => {
    const today = todayVn();
    const late = x.due && x.status !== 'done' && x.due < today;
    const dueNow = x.due && x.status !== 'done' && x.due === today;
    return (
      <div key={x.id} className={`tb-card p-${x.priority}${x.status === 'done' ? ' is-done' : ''}${drag?.id === x.id ? ' dragging' : ''}`}
        data-tb-card={x.id} draggable={x.canEdit}
        onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(x.id)); setDrag({ id: x.id, from: x.status }); }}
        onDragEnd={() => { setDrag(null); setOver(null); }}
        onClick={() => setOpen({ id: x.id })}>
        {x.status === 'done' && <div className="tb-donebadge"><CircleCheck size={13} /> {t('xTask.completed')}</div>}
        <div className="tb-card-top">
          <div className="tb-card-title">{x.title}</div>
          {face(x) && <span title={face(x).name}><UserAvatar url={face(x).avatar} name={face(x).name} size={24} /></span>}
        </div>
        {x.snippet && <div className="tb-card-snip">{x.snippet}</div>}
        <div className="tb-card-meta">
          {x.requested && <span className="tb-req" title={t('xTask.f_requested')}><Inbox size={12} /> {fmtDay(x.requested)}</span>}
          {x.due && <span className={`tb-due${late ? ' late' : dueNow ? ' today' : ''}`} title={late ? t('xTask.overdue') : dueNow ? t('xTask.dueToday') : ''}><Calendar size={12} /> {fmtDay(x.due)}</span>}
          {x.noRush && <span className="tb-norush" title={t('xTask.f_noRushHint')}><Coffee size={11} /> {t('xTask.f_noRush')}</span>}
          {x.noRush && x.planned && <span className="tb-planned" title={t('xTask.f_planned')}><CalendarClock size={12} /> {fmtDay(x.planned)}</span>}
          {x.estDays > 0 && <span className="tb-req" title={t('xTask.f_est')}><Timer size={12} /> {t('xTask.estN', { n: x.estDays })}</span>}
          {x.repeatFreq && <span className="tb-rep" title={t('xTask.f_repeat')}><Repeat size={11} /> {repeatLabel(t, x.repeatFreq, x.repeatEvery)}</span>}
          {x.priority !== 'normal' && <span className={`tb-prio ${x.priority}`}>{t(`xTask.p_${x.priority}`)}</span>}
          {(x.tags || []).map((g) => <span key={g} className="tb-tag">{g}</span>)}
          {x.checks > 0 && <span className="tb-cnt"><CheckSquare size={12} /> {t('xTask.checkCount', { done: x.checksDone, all: x.checks })}</span>}
          {x.comments > 0 && <span className="tb-cnt"><MessageSquare size={12} /> {x.comments}</span>}
          {x.files > 0 && <span className="tb-cnt"><Paperclip size={12} /> {x.files}</span>}
          {x.shares > 0 && <span className="tb-cnt"><Share2 size={12} /> {x.shares}</span>}
          {!x.canEdit && <span className="tb-cnt"><Lock size={12} /></span>}
        </div>
      </div>
    );
  };
  const column = (c) => {
    const Icon = c.icon;
    const list = byCol[c.key] || [];
    // only an owner of the board starts tasks (Jay 30/10: "Don't allow others to register theirs" — taskBoard.js needOwner)
    const canAdd = !!ppl?.canCreate && (board === 'me' || (ppl?.boardsSharedWithMe || []).some((b) => String(b.id) === String(board) && b.canEdit));
    return (
      <section key={c.key} className={`tb-col c-${c.key}${over && over.col === c.key ? ' over' : ''}`} data-tb-col={c.key}
        onDragOver={(e) => { if (!drag) return; e.preventDefault(); const index = indexAt(c.key, e.clientY); if (!over || over.col !== c.key || over.index !== index) setOver({ col: c.key, index }); }}
        onDrop={(e) => { e.preventDefault(); drop(c.key); }}>
        <header className="tb-col-head"><Icon size={16} /> <span>{t(`xTask.col_${c.key}`)}</span><b>{list.length}</b></header>
        <div className="tb-col-body" ref={(el) => { colRefs.current[c.key] = el; }}>
          {list.map((x, i) => (
            <React.Fragment key={x.id}>
              {over && over.col === c.key && over.index === i && drag && !order && <div className="tb-slot" />}
              {card(x)}
            </React.Fragment>
          ))}
          {over && over.col === c.key && over.index >= list.filter((x) => x.id !== drag?.id).length && drag && !order && <div className="tb-slot" />}
          {!list.length && !drag && <div className="tb-empty">{t('xTask.noTasks')}</div>}
          {canAdd && (quick && quick.col === c.key ? (
            <input className="tb-quick" autoFocus value={quick.text} placeholder={t('xTask.quickPh')} data-tb-quick={c.key}
              onChange={(e) => setQuick({ col: c.key, text: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') addQuick(c.key); if (e.key === 'Escape') setQuick(null); }}
              onBlur={() => { if (!quick.text.trim()) setQuick(null); }} />
          ) : (
            <button type="button" className="tb-add" onClick={() => setQuick({ col: c.key, text: '' })} title={t('xTask.addHere')} data-tb-add={c.key}><Plus size={16} /></button>
          ))}
        </div>
      </section>
    );
  };

  const boards = [{ key: 'me', label: t('xTask.myBoard') },
    ...(ppl?.boardsSharedWithMe || []).map((b) => ({ key: String(b.id), label: t('xTask.boardOf', { name: b.name || t('xTask.nameLess') }), avatar: b.avatar, name: b.name })),
    { key: 'shared', label: t('xTask.sharedWithMe') }];

  return (
    <div className="pa-page">
      <Header />
      <main className="pa-main tb-page">
        <div className="mh-titlerow">
          <div className="pa-title"><h2>{t('xTask.title')}</h2><p>{t('xTask.sub')}</p></div>
          {/* setViewRaw, not setView: the tour must not overwrite the view this person keeps */}
          <ModuleHelp steps={TOUR} tourKey="taskBoard" ready={!!tasks && !!ppl} onBeforeTour={() => setViewRaw('board')} />
        </div>
        <div className="tb-bar">
          {view !== 'day' && <div className="tb-boards" role="tablist">
            {boards.map((b) => (
              <button key={b.key} type="button" role="tab" aria-selected={board === b.key} className={`tb-board${board === b.key ? ' on' : ''}`}
                onClick={() => setBoard(b.key)} data-tb-board={b.key}>
                {b.avatar !== undefined && <UserAvatar url={b.avatar} name={b.name} size={18} />}{b.label}
              </button>
            ))}
          </div>}
          <div className="tl-seg tb-viewseg" role="group" aria-label={t('xTask.view')}>
            <button type="button" className={view === 'board' ? 'on' : ''} onClick={() => setView('board')} data-tb-view="board"><SquareKanban size={14} /> {t('xTask.v_board')}</button>
            <button type="button" className={view === 'timeline' ? 'on' : ''} onClick={() => setView('timeline')} data-tb-view="timeline"><ChartGantt size={14} /> {t('xTask.v_timeline')}</button>
            <button type="button" className={view === 'day' ? 'on' : ''} onClick={() => setView('day')} data-tb-view="day"><CalendarDays size={14} /> {t('xTask.v_day')}</button>
          </div>
          <span className="tb-grow" />
          {view !== 'day' && <>
          <label className="tb-search"><Search size={14} /><input value={q} placeholder={t('xTask.search')} onChange={(e) => setQ(e.target.value)} /></label>
          <select className="tb-in tb-filter" value={prio} onChange={(e) => setPrio(e.target.value)}>
            <option value="">{t('xTask.allPrio')}</option><option value="hi">{t('xTask.onlyUrgent')}</option>
          </select>
          {board === 'me' && (
            <select className="tb-in tb-filter" value={whose} onChange={(e) => setWhose(e.target.value)}>
              <option value="">{t('xTask.anyone')}</option><option value="given">{t('xTask.assignedMe')}</option><option value="made">{t('xTask.createdMe')}</option>
            </select>
          )}
          </>}
          {view === 'board' && <select className="tb-in tb-filter" value={order} onChange={(e) => setOrder(e.target.value)} data-tb-order="1">
            <option value="">{t('xTask.sortMine')}</option><option value="due">{t('xTask.sortDue')}</option><option value="prio">{t('xTask.sortPrio')}</option>
          </select>}
          {inbox?.on && <button type="button" className="pa-btn" onClick={() => setSugOpen(true)} data-tb-inbox="1"><Sparkles size={14} /> {t('xTask.in_btn')}{inbox.suggestions?.length ? <span className="tb-sug-count">{inbox.suggestions.length}</span> : null}</button>}
          {ppl?.canCreate && <button type="button" className="pa-btn" onClick={() => setShareOpen(true)} data-tb-shareboard="1"><Share2 size={14} /> {t('xTask.shareBoard')}{ppl?.mySharedTo?.length ? ` (${ppl.mySharedTo.length})` : ''}</button>}
          {ppl?.canCreate && <button type="button" className="pa-btn primary" onClick={() => setOpen({ id: null, status: 'todo' })} data-tb-new="1"><Plus size={14} /> {t('xTask.newTask')}</button>}
        </div>
        {err && <div className="tb-err" onClick={() => setErr('')}>{err}</div>}
        {note && <div className="tb-note" onClick={() => setNote(null)} data-tb-note="1"><Repeat size={14} /> {t('xTask.noteNext', { title: note.title, date: fmtDay(note.due || note.requested) })}</div>}
        {view === 'day' ? <DayView t={t} onOpen={(tid) => setOpen({ id: tid })} /> : !tasks ? <p className="tb-hint">{t('xTask.loading')}</p> : (
          view === 'timeline' ? <TaskTimeline t={t} tasks={shown} meId={meId} onOpen={(tid) => setOpen({ id: tid })} />
            : <div className="tb-board-grid">{COLS.map((c) => column(c))}</div>
        )}
      </main>
      {open && ppl && (
        <TaskModal t={t} id={open.id} people={ppl.people} meId={meId} initialStatus={open.status} frequent={ppl.frequent || {}} prefill={open.prefill || null} focusComment={open.comment || null}
          onCreated={fromSug ? (tid) => { call('POST', `/inbox/${fromSug.id}/accept`, { taskId: tid }).then(loadInbox).catch((e) => setErr(e.message)); } : null}
          onClose={() => { setOpen(null); if (fromSug) { setFromSug(null); setSugOpen(true); } }} onChanged={() => { loadBoard(); }} onNote={setNote} />
      )}
      {sugOpen && inbox?.on && !open && (
        <SuggestModal t={t} data={inbox} onClose={() => setSugOpen(false)} onReload={loadInbox} onCreate={sugToTask} />
      )}
      {shareOpen && ppl && (
        <BoardShareModal t={t} people={ppl.people} meId={meId} current={ppl.mySharedTo} onClose={() => setShareOpen(false)} onSaved={loadPeople} />
      )}
    </div>
  );
}
